import {
  CreateOutreachProspectSchema,
  OutreachStageSchema,
  type Admin,
  type CreateOutreachProspectDto,
  type OutreachProspect,
} from "@lumika/shared";
import { z } from "zod";
import {
  FormField,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  type UseZodFormResult,
} from "@lumika/ui";
import { STAGE_LABELS, STAGE_ORDER, datetimeLocalToIso, isoToDatetimeLocal } from "./outreach-ui";
import { parseTags } from "./import-parse";

const UNASSIGNED = "__unassigned__";

// Form-local schema: text inputs surface "" rather than null, and only
// email is format-checked (matching the server DTO). `toProspectPayload`
// converts "" → null before the strict server schema parses it.
export const ProspectFormSchema = z.object({
  companyName: z.string().trim().min(1, "Company name is required.").max(200),
  contactName: z.string().trim().max(200).optional(),
  contactTitle: z.string().trim().max(200).optional(),
  email: z.union([z.literal(""), z.string().email("Invalid email.")]).optional(),
  phone: z.string().trim().max(40).optional(),
  websiteUrl: z.string().trim().max(200).optional(),
  linkedinUrl: z.string().trim().max(200).optional(),
  city: z.string().trim().max(120).optional(),
  state: z.string().trim().max(40).optional(),
  source: z.string().trim().max(120).optional(),
  stage: OutreachStageSchema,
  ownerAdminId: z.string().optional(),
  nextFollowUp: z.string().optional(),
  // datetime-local value (YYYY-MM-DDTHH:mm, local time) — converted to ISO on save.
  demoAt: z.string().optional(),
  // Free-text in the form (semicolon/comma-separated); parsed to an array on save.
  tags: z.string().trim().optional(),
});

export type ProspectFormValues = z.input<typeof ProspectFormSchema>;

export const EMPTY_PROSPECT: ProspectFormValues = {
  companyName: "",
  contactName: "",
  contactTitle: "",
  email: "",
  phone: "",
  websiteUrl: "",
  linkedinUrl: "",
  city: "",
  state: "",
  source: "",
  stage: "new",
  ownerAdminId: "",
  nextFollowUp: "",
  demoAt: "",
  tags: "",
};

export function toProspectEditValues(p: OutreachProspect): ProspectFormValues {
  return {
    companyName: p.companyName,
    contactName: p.contactName ?? "",
    contactTitle: p.contactTitle ?? "",
    email: p.email ?? "",
    phone: p.phone ?? "",
    websiteUrl: p.websiteUrl ?? "",
    linkedinUrl: p.linkedinUrl ?? "",
    city: p.city ?? "",
    state: p.state ?? "",
    source: p.source ?? "",
    stage: p.stage,
    ownerAdminId: p.ownerAdminId ?? "",
    // Stored at 00:00:00Z, so the date slice round-trips with the input.
    nextFollowUp: p.nextFollowUpAt ? p.nextFollowUpAt.slice(0, 10) : "",
    // Convert stored UTC timestamptz to local datetime-local value for the input.
    demoAt: isoToDatetimeLocal(p.demoAt),
    // Editable as a single string; re-parsed to an array on save.
    tags: p.tags.join("; "),
  };
}

export function toProspectPayload(values: ProspectFormValues): CreateOutreachProspectDto {
  const nz = (s?: string): string | null => (s && s.trim() ? s.trim() : null);
  return CreateOutreachProspectSchema.parse({
    companyName: values.companyName.trim(),
    contactName: nz(values.contactName),
    contactTitle: nz(values.contactTitle),
    email: nz(values.email),
    phone: nz(values.phone),
    websiteUrl: nz(values.websiteUrl),
    linkedinUrl: nz(values.linkedinUrl),
    city: nz(values.city),
    state: nz(values.state),
    source: nz(values.source),
    stage: values.stage,
    ownerAdminId: values.ownerAdminId ? values.ownerAdminId : null,
    nextFollowUpAt: values.nextFollowUp
      ? new Date(`${values.nextFollowUp}T00:00:00Z`).toISOString()
      : null,
    demoAt: datetimeLocalToIso(values.demoAt ?? ""),
    tags: parseTags(values.tags),
  });
}

type Props = {
  values: ProspectFormValues;
  errors: UseZodFormResult<ProspectFormValues>["errors"];
  setField: UseZodFormResult<ProspectFormValues>["setField"];
  admins: Admin[];
  autoFocus?: boolean;
};

/** Shared editable field set for a prospect, used by the create dialog
 *  and the detail page's edit form. */
export function ProspectFormFields({ values, errors, setField, admins, autoFocus }: Props) {
  return (
    <div className="grid gap-5">
      <FieldGroup title="Company">
        <FormField className="sm:col-span-2" label="Company name" required error={errors.companyName}>
          <Input
            value={values.companyName}
            onChange={(e) => setField("companyName", e.target.value)}
            autoFocus={autoFocus}
            placeholder="Sunshine Auto Group"
          />
        </FormField>
        <FormField label="Website" optional error={errors.websiteUrl}>
          <Input
            value={values.websiteUrl ?? ""}
            onChange={(e) => setField("websiteUrl", e.target.value)}
            placeholder="sunshineauto.com"
          />
        </FormField>
        <FormField label="Source" optional error={errors.source}>
          <Input
            value={values.source ?? ""}
            onChange={(e) => setField("source", e.target.value)}
            placeholder="Referral, cold, event..."
          />
        </FormField>
        <FormField label="City" optional error={errors.city}>
          <Input value={values.city ?? ""} onChange={(e) => setField("city", e.target.value)} />
        </FormField>
        <FormField label="State" optional error={errors.state}>
          <Input
            value={values.state ?? ""}
            onChange={(e) => setField("state", e.target.value)}
            placeholder="CA"
          />
        </FormField>
        <FormField
          className="sm:col-span-2"
          label="Tags"
          optional
          error={errors.tags}
          description="Semicolon-separated, e.g. independent; bdc-hiring; sunbelt"
        >
          <Input
            value={values.tags ?? ""}
            onChange={(e) => setField("tags", e.target.value)}
            placeholder="independent; bdc-hiring; sunbelt"
          />
        </FormField>
      </FieldGroup>

      <FieldGroup title="Primary contact">
        <FormField label="Name" optional error={errors.contactName}>
          <Input
            value={values.contactName ?? ""}
            onChange={(e) => setField("contactName", e.target.value)}
          />
        </FormField>
        <FormField label="Title" optional error={errors.contactTitle}>
          <Input
            value={values.contactTitle ?? ""}
            onChange={(e) => setField("contactTitle", e.target.value)}
            placeholder="GM, Owner..."
          />
        </FormField>
        <FormField label="Email" optional error={errors.email}>
          <Input
            type="email"
            value={values.email ?? ""}
            onChange={(e) => setField("email", e.target.value)}
          />
        </FormField>
        <FormField label="Phone" optional error={errors.phone}>
          <Input
            type="tel"
            value={values.phone ?? ""}
            onChange={(e) => setField("phone", e.target.value)}
          />
        </FormField>
        <FormField className="sm:col-span-2" label="LinkedIn" optional error={errors.linkedinUrl}>
          <Input
            value={values.linkedinUrl ?? ""}
            onChange={(e) => setField("linkedinUrl", e.target.value)}
            placeholder="linkedin.com/company/... or /in/..."
          />
        </FormField>
      </FieldGroup>

      <FieldGroup title="Pipeline">
        <FormField label="Stage" required error={errors.stage}>
          <Select
            value={values.stage}
            onValueChange={(v) => setField("stage", v as ProspectFormValues["stage"])}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STAGE_ORDER.map((stage) => (
                <SelectItem key={stage} value={stage}>
                  {STAGE_LABELS[stage]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FormField>
        <FormField label="Owner" optional error={errors.ownerAdminId}>
          <Select
            value={values.ownerAdminId ? values.ownerAdminId : UNASSIGNED}
            onValueChange={(v) => setField("ownerAdminId", v === UNASSIGNED ? "" : v)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Unassigned" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={UNASSIGNED}>Unassigned</SelectItem>
              {admins.map((admin) => (
                <SelectItem key={admin.id} value={admin.id}>
                  {admin.displayName ?? admin.email}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FormField>
        <FormField
          className="sm:col-span-2"
          label="Demo date & time"
          optional
          error={errors.demoAt}
        >
          <Input
            type="datetime-local"
            value={values.demoAt ?? ""}
            onChange={(e) => setField("demoAt", e.target.value)}
          />
        </FormField>
        <FormField
          className="sm:col-span-2"
          label="Next follow-up"
          optional
          error={errors.nextFollowUp}
          description={`Drives the "due" queue and the daily reminder.`}
        >
          <Input
            type="date"
            value={values.nextFollowUp ?? ""}
            onChange={(e) => setField("nextFollowUp", e.target.value)}
          />
        </FormField>
      </FieldGroup>
    </div>
  );
}

function FieldGroup({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <fieldset className="grid gap-3">
      <legend className="text-xs font-bold uppercase tracking-[0.12em] text-muted-foreground">
        {title}
      </legend>
      <div className="grid gap-4 sm:grid-cols-2">{children}</div>
    </fieldset>
  );
}
